import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { htmlSafe } from '@ember/template';
import WidgetTile from './widget-tile.gts';
import {
  MIN_WIDGET_HEIGHT,
  MIN_WIDGET_WIDTH,
  RESIZE_SNAP,
  snapDimension,
  type WidgetConfig,
  type WidgetLayout,
  type BuildFilterConfig,
  type UserFilterConfig,
} from '../../utils/dashboard-builder/widget-config-types.ts';
import type { Drilldown, DrilldownMode } from '../../utils/dashboard-builder/drilldown.ts';

// WidgetGrid – dashboard canvas with a snap grid. Widgets are absolutely
// positioned (overlaps allowed); dragging the tile header moves a widget and
// the bottom-right handle resizes it, both snapping to RESIZE_SNAP steps. A
// dotted snap grid is shown on the canvas during either gesture. Pointer
// capture keeps all events element-local.

interface WidgetGridSignature {
  Args: {
    widgets: WidgetConfig[];
    selectedWidgetId?: string;
    editable?: boolean;
    companyId?: string;
    buildFilters?: BuildFilterConfig;
    userFilters?: UserFilterConfig;
    /** Viewer changed the user filters away from the saved defaults (view mode). */
    userFiltersModified?: boolean;
    /** Active drilldowns; each tile applies the ones for its dataset (view mode). */
    drilldowns?: Drilldown[];
    /** A click on a widget's breakdown value; `set` replaces without the toggle-off, `toggle` clears a re-selected value. */
    onDrilldown?: (drilldown: Drilldown, mode: DrilldownMode) => void;
    onSelect?: (id: string) => void;
    onRemove?: (id: string) => void;
    onUpdateLayout?: (id: string, layout: WidgetLayout) => void;
    /** Expand a widget to fill the canvas (view mode). */
    onExpand?: (id: string) => void;
    /** Per-widget CSV download (view mode); `undefined` hides the control on that tile. */
    exportActionFor?: (widget: WidgetConfig) => (() => void | Promise<unknown>) | undefined;
    /** Every tile's download stays visible but inert — e.g. no company in scope. */
    exportDisabled?: boolean;
    /** Tooltip explaining `exportDisabled`. */
    exportDisabledReason?: string;
    /** A per-widget reason its download stays inert (it would lose data); wins over `exportDisabledReason`. */
    exportBlockedReasonFor?: (widget: WidgetConfig) => string | undefined;
  };
  Element: HTMLDivElement;
}

interface Interaction {
  kind: 'move' | 'resize';
  id: string;
  startX: number;
  startY: number;
  origin: WidgetLayout;
  dx: number;
  dy: number;
}

export default class WidgetGrid extends Component<WidgetGridSignature> {
  @tracked interaction: Interaction | null = null;

  get isInteracting() {
    return this.interaction !== null;
  }

  /** Layout with the in-flight drag/resize applied (committed only on pointerup). */
  effectiveLayout = (widget: WidgetConfig): WidgetLayout => {
    const i = this.interaction;
    const layout = widget.layout;
    if (!i || i.id !== widget.id) return layout;
    return this.appliedLayout(i);
  };

  styleFor = (widget: WidgetConfig) => {
    const l = this.effectiveLayout(widget);
    const active = this.interaction?.id === widget.id;
    return htmlSafe(
      `left:${l.x}px; top:${l.y}px; width:${l.w}px; height:${l.h}px; z-index:${active ? 30 : 1}; touch-action:none;`
    );
  };

  get canvasStyle() {
    let maxBottom = 0;
    for (const w of this.args.widgets) {
      const l = this.effectiveLayout(w);
      maxBottom = Math.max(maxBottom, l.y + l.h);
    }
    return htmlSafe(`min-height:${maxBottom + 160}px;`);
  }

  get snapGridStyle() {
    // The radial-gradient dot renders at the CENTER of each background tile, so
    // shift the tiles by half the pitch to land dots exactly on snap points.
    const offset = -RESIZE_SNAP / 2;
    return htmlSafe(
      `background-image: radial-gradient(circle, rgba(148,163,184,0.55) 1.5px, transparent 1.5px);` +
        `background-size: ${RESIZE_SNAP}px ${RESIZE_SNAP}px;` +
        `background-position: ${offset}px ${offset}px;`
    );
  }

  // ── pointer interaction (capture-based; no global listeners) ───────────────
  handleMoveDown = (widget: WidgetConfig, event: PointerEvent) => {
    if (!this.args.editable) return;
    const target = event.target as HTMLElement;
    // Only the tile header is a drag region; leave its buttons clickable.
    if (target.closest('button, select, input')) return;
    if (!target.closest('[data-drag-handle]')) return;
    const wrapper = event.currentTarget as HTMLElement;
    wrapper.setPointerCapture(event.pointerId);
    this.interaction = {
      kind: 'move',
      id: widget.id,
      startX: event.clientX,
      startY: event.clientY,
      origin: { ...widget.layout },
      dx: 0,
      dy: 0,
    };
  };

  handleResizeDown = (widget: WidgetConfig, event: PointerEvent) => {
    if (!this.args.editable) return;
    event.stopPropagation();
    event.preventDefault();
    const wrapper = (event.currentTarget as HTMLElement).closest(
      '[data-widget-wrapper]'
    ) as HTMLElement | null;
    if (!wrapper) return;
    wrapper.setPointerCapture(event.pointerId);
    this.interaction = {
      kind: 'resize',
      id: widget.id,
      startX: event.clientX,
      startY: event.clientY,
      origin: { ...widget.layout },
      dx: 0,
      dy: 0,
    };
  };

  handlePointerMove = (event: PointerEvent) => {
    const i = this.interaction;
    if (!i) return;
    this.interaction = { ...i, dx: event.clientX - i.startX, dy: event.clientY - i.startY };
  };

  handlePointerUp = (widget: WidgetConfig) => {
    const i = this.interaction;
    if (!i || i.id !== widget.id) return;
    this.interaction = null;
    // Skip plain clicks and gestures that snap back to the same cell so they
    // don't dirty the dashboard.
    const next = this.appliedLayout(i);
    const o = i.origin;
    if (next.x === o.x && next.y === o.y && next.w === o.w && next.h === o.h) return;
    this.args.onUpdateLayout?.(widget.id, next);
  };

  /** Snap a position component to the grid, floored at the canvas edge. */
  private snapPosition(value: number): number {
    return snapDimension(value, 0);
  }

  private appliedLayout(i: Interaction): WidgetLayout {
    if (i.kind === 'move') {
      return {
        ...i.origin,
        x: this.snapPosition(i.origin.x + i.dx),
        y: this.snapPosition(i.origin.y + i.dy),
      };
    }
    return {
      ...i.origin,
      w: snapDimension(i.origin.w + i.dx, MIN_WIDGET_WIDTH),
      h: snapDimension(i.origin.h + i.dy, MIN_WIDGET_HEIGHT),
    };
  }

  selectWidget = (id: string) => {
    this.args.onSelect?.(id);
  };

  removeWidget = (id: string) => {
    this.args.onRemove?.(id);
  };

  exportFor = (widget: WidgetConfig) => this.args.exportActionFor?.(widget);

  exportDisabledFor = (widget: WidgetConfig) =>
    Boolean(this.args.exportDisabled || this.args.exportBlockedReasonFor?.(widget));

  exportReasonFor = (widget: WidgetConfig) =>
    this.args.exportBlockedReasonFor?.(widget) ?? this.args.exportDisabledReason;

  <template>
    {{! Drag-to-move and snap-resize require pointerdown to start the gesture. }}
    {{! template-lint-disable no-pointer-down-event-binding }}
    <div data-test-widget-grid class="relative" style={{this.canvasStyle}} ...attributes>
      {{#if this.isInteracting}}
        <div
          data-test-snap-grid
          class="pointer-events-none absolute inset-0"
          style={{this.snapGridStyle}}
        ></div>
      {{/if}}

      {{#each @widgets key="id" as |widget|}}
        <div
          data-widget-wrapper
          class="absolute"
          style={{this.styleFor widget}}
          {{on "pointerdown" (fn this.handleMoveDown widget)}}
          {{on "pointermove" this.handlePointerMove}}
          {{on "pointerup" (fn this.handlePointerUp widget)}}
          {{on "pointercancel" (fn this.handlePointerUp widget)}}
        >
          <WidgetTile
            @widget={{widget}}
            @selected={{eq widget.id @selectedWidgetId}}
            @editable={{@editable}}
            @companyId={{@companyId}}
            @buildFilters={{@buildFilters}}
            @userFilters={{@userFilters}}
            @userFiltersModified={{@userFiltersModified}}
            @drilldowns={{@drilldowns}}
            @onDrilldown={{@onDrilldown}}
            @onSelect={{fn this.selectWidget widget.id}}
            @onRemove={{fn this.removeWidget widget.id}}
            @onExpand={{if @onExpand (fn @onExpand widget.id)}}
            @onExport={{this.exportFor widget}}
            @exportDisabled={{this.exportDisabledFor widget}}
            @exportDisabledReason={{this.exportReasonFor widget}}
          />
          {{#if @editable}}
            <button
              type="button"
              data-test-resize-handle
              aria-label="Resize widget"
              class="absolute bottom-0 right-0 flex h-5 w-5 cursor-se-resize items-end justify-end tio-text-micro leading-none tio-fg-muted"
              {{on "pointerdown" (fn this.handleResizeDown widget)}}
            >◢</button>
          {{/if}}
        </div>
      {{/each}}
    </div>
  </template>
}
